import React, { useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';

import useCenterService from '../../app/services/hooks/useCenterService';
import { CenterDataForm, CenterFilters } from '../../app/models/Center';
import CenterDataTable from './components/tables/CenterDataTable';
import CenterFilter from './components/tables/CenterFilter';
import CenterCreate from './components/CenterCreate';
import CenterEdit from './components/CenterEdit';

const breadcrumbs: BreadcrumbsItem[] = [
  { name: 'centros', url: '/centers', isActive: true }
];

const Centers = () => {
  const { navigationPage } = useNavigationPage();
  const { fetchingGetCenters, getCenters, deleteCenter } = useCenterService();

  const [centers, setCenters] = useState<CenterDataForm[]>([]);
  const [centerIdEdit, setCenterIdEdit] = useState<number>(-1);
  const [showingEdit, setShowingEdit] = useState(false);
  const [showingCreate, setShowingCreate] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const confirm = useReactConfirmAlert();
  const [centerFilter, setCenterFilter] = useState<CenterFilters>({
    page: 1, per_page: 10, sort: 'id', order: 'desc',
    society_id: null, name: '', address: '', is_active: ''
  });

  useEffect(() => { getAll(); }, [centerFilter.page, centerFilter.per_page, centerFilter.sort, centerFilter.order]);

  const getAll = () => {
    getCenters(centerFilter, {
      onSuccess: (response: ServiceResponse) => {
        setCenters(response.data.centers);
        setTotalRows(response.data.total_rows);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message, { autoClose: 2000 });
        if (response.data?.to_dashboard) navigationPage('/');
      }
    });
  };

  const destroy = (id: number) => {
    const _text = `Está a punto de eliminar el centro #${id}`;
    confirm.requestConfirmation({
      title: '¿Estás seguro?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Sí, confirmar',
          onClick: () => {
            deleteCenter(id, {
              onSuccess: (response: ServiceResponse) => {
                confirm.successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                confirm.errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              confirm.infoAlert({ title: 'Cancelado', message: 'El centro no se ha eliminado.' });
            }, 0);
          }
        }
      }
    });
  };

  const showEdit = (id: number) => { setShowingEdit(true); setCenterIdEdit(id); };
  const hideEdit = () => { setShowingEdit(false); setCenterIdEdit(-1); };
  const showCreate = () => setShowingCreate(true);
  const hideCreate = () => setShowingCreate(false);
  const reloadTable = () => getAll();

  // (Opcional) Cargar sociedades para el filtro
  // const [societies, setSocieties] = useState<{id:number; name:string}[]>([]);
  // useEffect(() => { loadSocieties(); }, []);
  // const loadSocieties = () => useSocietyService().getAllSelect(...)

  return (
    <>
      <Breadcrumbs pageSection="Centros" breadcrumbs={breadcrumbs} />

      <DefaultCard>
        <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} title="Nuevo centro" />} />

        <div className="row mt-3">
          <div className="col">
            <CenterFilter
              filter={centerFilter}
              setFilter={setCenterFilter}
              onSearch={reloadTable}
            />
          </div>
        </div>

        <CenterDataTable
          centers={centers}
          edit={showEdit}
          destroy={destroy}
          loading={fetchingGetCenters}
          filter={centerFilter}
          setFilter={setCenterFilter}
          totalRows={totalRows}
          callbackSuccess={reloadTable}
        />
      </DefaultCard>

      {showingCreate && (
        <DefaultModal show={showingCreate} handleClose={hideCreate} title="Crear centro" backdrop={true} showFooter={false}>
          <CenterCreate onSaved={() => { reloadTable(); hideCreate(); }} onCancel={hideCreate} />
        </DefaultModal>
      )}

      {showingEdit && (
        <DefaultModal show={showingEdit} handleClose={hideEdit} title="Editar centro" backdrop={true} showFooter={false}>
          <CenterEdit centerId={centerIdEdit} onSaved={() => { reloadTable(); hideEdit(); }} onCancel={hideEdit} onError={hideEdit} />
        </DefaultModal>
      )}
    </>
  );
};

export default Centers;